import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaSave, FaTimes, FaInfoCircle, 
  FaCalendarAlt, FaTruck, FaUsers, FaGavel,
  FaBuilding, FaMapMarkerAlt, FaWeightHanging
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

function CreateTender({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  const [formData, setFormData] = useState({
    job_id: '',
    tender_type: 'ad_hoc',
    bidding_deadline: '',
    target_rate: '',
    min_trucks_required: '',
    number_of_winners: 1,
    duration_months: 1,
    monthly_volume_mt: '',
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/jobs`);
      const data = await response.json();
      if (data.success) {
        // Only show jobs that aren't already tenders
        const availableJobs = data.jobs.filter(j => !j.is_tender && j.status !== 'completed');
        setJobs(availableJobs);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      toast.error('Failed to load jobs');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleJobSelect = (e) => {
    const jobId = e.target.value;
    setFormData(prev => ({ ...prev, job_id: jobId }));
    const job = jobs.find(j => j.id === jobId);
    setSelectedJob(job);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.job_id || !formData.bidding_deadline) {
      toast.error('Please select a job and set a bidding deadline');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/tenders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          created_by: user.id,
          total_volume_mt: selectedJob?.total_quantity_mt,
          monthly_volume_mt: formData.tender_type === 'framework' ? formData.monthly_volume_mt : null,
          duration_months: formData.tender_type === 'framework' ? formData.duration_months : 1,
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Tender ${data.tender.tender_number} created successfully!`);
        navigate('/tender-dashboard');
      } else {
        toast.error(data.error || 'Failed to create tender');
      }
    } catch (err) {
      console.error('Error creating tender:', err);
      toast.error('Failed to create tender');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/tender-dashboard');
  };

  return (
    <AdminLayout activeTab="tenders" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={handleCancel}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: '#0A1929' }}
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
            <FaGavel className="inline mr-2" style={{ color: '#00A86B' }} />
            Create Tender
          </h1>
          <p className="text-sm" style={{ color: '#4A5568' }}>Create a new tender for subcontractors to bid on</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
        <form onSubmit={handleSubmit}>
          {/* Job Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Job <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.job_id}
              onChange={handleJobSelect}
              className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
              style={{ borderColor: '#E8ECEF' }}
              required
            >
              <option value="">Select a job...</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>
                  {job.job_number} - {job.commodity} ({job.total_quantity_mt} MT)
                </option>
              ))}
            </select>
          </div>

          {/* Selected Job Preview */}
          {selectedJob && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-navy mb-2">Job Details</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Job Number</p>
                  <p className="font-medium">{selectedJob.job_number}</p>
                </div>
                <div>
                  <p className="text-gray-500">Commodity</p>
                  <p className="font-medium">{selectedJob.commodity}</p>
                </div>
                <div>
                  <p className="text-gray-500">Quantity</p>
                  <p className="font-medium">{selectedJob.total_quantity_mt} MT</p>
                </div>
                <div>
                  <p className="text-gray-500">Route</p>
                  <p className="font-medium">{selectedJob.origin} → {selectedJob.destination}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tender Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tender Type <span className="text-red-500">*</span>
              </label>
              <select
                name="tender_type"
                value={formData.tender_type}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                required
              >
                <option value="ad_hoc">Ad-Hoc (Single Job)</option>
                <option value="framework">Framework (12 Months)</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {formData.tender_type === 'ad_hoc' 
                  ? 'One-time job, multiple subbies can bid, 1+ winners' 
                  : 'Long-term contract, multiple winners, volume split'}
              </p>
            </div>

            {/* Bidding Deadline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bidding Deadline <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="bidding_deadline"
                value={formData.bidding_deadline}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                required
              />
            </div>

            {/* Target Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Rate (R/MT)
              </label>
              <input
                type="number"
                step="0.01"
                name="target_rate"
                value={formData.target_rate}
                onChange={handleChange}
                placeholder="e.g. 450"
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
              <p className="text-xs text-gray-400 mt-1">Subbies will see this as a guide</p>
            </div>

            {/* Min Trucks Required */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Trucks Required
              </label>
              <input
                type="number"
                name="min_trucks_required"
                value={formData.min_trucks_required}
                onChange={handleChange}
                placeholder="e.g. 5"
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>

            {/* Number of Winners */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Winners <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max="10"
                name="number_of_winners"
                value={formData.number_of_winners}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                style={{ borderColor: '#E8ECEF' }}
                required
              />
            </div>

            {/* Framework Specific Fields */}
            {formData.tender_type === 'framework' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (Months) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    name="duration_months"
                    value={formData.duration_months}
                    onChange={handleChange}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                    style={{ borderColor: '#E8ECEF' }}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Volume (MT) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="monthly_volume_mt"
                    value={formData.monthly_volume_mt}
                    onChange={handleChange}
                    placeholder="e.g. 10000"
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                    style={{ borderColor: '#E8ECEF' }}
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Total volume will be {parseFloat(formData.monthly_volume_mt || 0) * parseFloat(formData.duration_months || 1)} MT</p>
                </div>
              </>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <FaInfoCircle className="text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">What happens next?</p>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• All subcontractors will be notified of the new tender</li>
                  <li>• Subbies can submit bids until the deadline</li>
                  <li>• You can view and compare bids on the Tender Dashboard</li>
                  <li>• Select winners and allocate volume</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ backgroundColor: '#F8F9FA', color: '#4A5568', border: '1px solid #E8ECEF' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#00A86B' }}
            >
              <FaSave size={14} />
              {loading ? 'Creating...' : 'Create Tender'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

export default CreateTender;
